import {
  scopedFleetAuthenticationCollection,
  useFetchEnergySystemRequiredScopes,
} from '@/reducers/teslaFleetAuthentication'
import { ID } from '@/types/model'
import { TeslaFleetAuthenticationScope } from '@/types/teslaFleetAuthentication'
import { fetchAuthenticated } from '@/utils/fetch'
import { useEffect, useState } from 'react'
import { useRouteMatch } from 'react-router-dom'

export enum DestinationBrand {
  Tesla = 'tesla',
}

type ScopedFleetAuthentication = {
  id: ID
  scopes: TeslaFleetAuthenticationScope[]
}

function findAuthenticationWithRequiredEnergyScopes(
  requiredScopes: TeslaFleetAuthenticationScope[],
  authentications: ScopedFleetAuthentication[],
): ScopedFleetAuthentication | undefined {
  return authentications.find((authentication) =>
    requiredScopes.every((scope) => authentication.scopes.includes(scope)),
  )
}

function useFetchScopedFleetAuthentications(): ScopedFleetAuthentication[] {
  const { data } = scopedFleetAuthenticationCollection.useFetch(
    'tesla-fleet-authentication/list',
  )

  return data
}

function useFetchRequiredEnergyScopes(): {
  requiredScopes: TeslaFleetAuthenticationScope[]
  isLoading: boolean
  error: Error | null
} {
  const [requiredScopes, setRequiredScopes] = useState<
    TeslaFleetAuthenticationScope[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchRequiredEnergyScopes = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const resp = await fetchAuthenticated(
        '/tesla-fleet-authentication/energy-systems/required-scopes',
      )

      if (!resp.ok) {
        throw new Error('Failed to fetch required energy scopes')
      }

      const data = await resp.json()
      if (
        !Array.isArray(data) ||
        data.some((scope) => typeof scope !== 'string')
      ) {
        throw new Error(
          'Required energy scopes should be an array of scope strings',
        )
      }

      setRequiredScopes(data)
    } catch (err) {
      setError(err as Error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchRequiredEnergyScopes()
  }, [])

  return { requiredScopes, isLoading, error }
}

export default function useViewModel() {
  const { url } = useRouteMatch()
  const {
    data: requiredScopes,
    isLoading,
    error,
  } = useFetchEnergySystemRequiredScopes()
  const scopedFleetAuthentications = useFetchScopedFleetAuthentications()

  const authenticationWithRequiredEnergyScopes =
    findAuthenticationWithRequiredEnergyScopes(
      requiredScopes,
      scopedFleetAuthentications,
    )

  function teslaDestination() {
    const permissionsStatementUrl = `${url}/tesla/permissions-statement`

    if (error !== null) {
      return permissionsStatementUrl
    }

    if (scopedFleetAuthentications.length === 0) {
      return permissionsStatementUrl
    }

    const authentication =
      authenticationWithRequiredEnergyScopes ?? scopedFleetAuthentications[0]

    return `/tesla-fleet-auth-redirect/energy-system/${authentication.id}`
  }

  const getSelectDestination = (
    brandOrDestination: DestinationBrand | string,
  ) => {
    if (brandOrDestination === DestinationBrand.Tesla) {
      return teslaDestination()
    }

    return brandOrDestination
  }

  return {
    getSelectDestination,
    isLoading,
    error,
  }
}
